import { useState } from "react";
import { useTranslation } from "react-i18next";
import { InteractionCategory, InteractionDefintion } from "./InteractionDefinitions";
import { useInteractionPickerIcons } from "./InteractionPickerIconHooks";

interface InteractionPickerDetailsProps {
  definition?: InteractionDefintion;
  getCategoryLabel: (category: InteractionCategory) => string;
}

export default function InteractionPickerDetails(
  props: InteractionPickerDetailsProps
) {
  const { definition, getCategoryLabel } = props;
  const { t } = useTranslation();
  const [getIcon] = useInteractionPickerIcons();
  // Tracked by src rather than as a flag, so one missing preview does not
  // suppress the next definition's working one.
  const [failedPreviewSrc, setFailedPreviewSrc] = useState<string | null>(null);

  if (!definition) {
    return (
      <div className="w-[400px] shrink-0 border-l border-creator-border bg-creator-primary-background p-[16px]" />
    );
  }

  const aliases = definition.searchAliases ?? [];
  const previewSrc = `/picker_previews/${definition.previewImage}`;

  const metaRow = (label: string, value: string) => (
    <div className="flex items-center justify-between gap-[12px] py-[9px] border-t border-creator-border">
      <span className="text-[11px] text-creator-text-sub">{label}</span>
      <span className="text-[11px] text-creator-text text-right truncate">
        {value}
      </span>
    </div>
  );

  return (
    <div className="w-[400px] shrink-0 border-l border-creator-border bg-creator-primary-background p-[16px] flex flex-col overflow-y-auto pit-quiet-scrollbar">
      <div className="text-[15px] font-semibold text-creator-text">
        {definition.name}
      </div>

      {/* Reads as a card now that the pane behind it is recessed. */}
      <div className="mt-[12px] h-[230px] rounded-[8px] border border-creator-border bg-creator-surface flex items-center justify-center overflow-hidden">
        {failedPreviewSrc === previewSrc ? (
          <span className="text-[40px] text-creator-text-sub flex">
            {getIcon(definition)}
          </span>
        ) : (
          <img
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            src={previewSrc}
            alt=""
            onError={() => setFailedPreviewSrc(previewSrc)}
          />
        )}
      </div>

      <div className="mt-[12px] text-[12px] leading-[18px] text-creator-text-sub">
        {t("interaction_descriptions." + definition.description)}
      </div>

      <div className="mt-auto pt-[16px]">
        {metaRow(
          t("interaction_picker_ui.category"),
          getCategoryLabel(definition.category)
        )}
        {aliases.length > 0 &&
          metaRow(
            t("interaction_picker_ui.also_found_as"),
            aliases.join(", ")
          )}
      </div>
    </div>
  );
}
